/**
 * CWR 1.3 — "Your Next Session" hero (mockup dashboard-mockup.html:1710–1752).
 *
 * One tap target (a single <button>) → the booking detail. Two modes:
 *   capacity != null (shared event) → capacity bar
 *   capacity == null (1:1)          → provider + location row
 */
import { InitialsAvatar } from '@/components/hub/InitialsAvatar';
import type { UpcomingBooking } from '@/types/dashboard';
import {
  capacityBooked,
  formatCardTime,
  formatHeroDate,
  formatLocationText,
  getHeroStatusLabel,
} from '../homeFormat';
import { CalendarIcon, ChevronRightIcon, ClockIcon, UsersIcon } from './icons';
import styles from '../BookingHubPage.module.css';

export interface NextSessionHeroProps {
  booking: UpcomingBooking;
  onOpen: () => void;
}

export function NextSessionHero({ booking, onOpen }: NextSessionHeroProps) {
  const isGroup = booking.capacity != null && booking.spotsRemaining != null;
  const booked = isGroup ? capacityBooked(booking.capacity!, booking.spotsRemaining!) : 0;
  const fillPct = isGroup && booking.capacity! > 0 ? (booked / booking.capacity!) * 100 : 0;

  return (
    <button
      type="button"
      className={styles.nextSession}
      data-testid="home-next-session"
      onClick={onOpen}
      aria-label={`View booking: ${booking.service.name}, ${formatHeroDate(booking.scheduledAt)}`}
    >
      <div className={styles.nextSessionTop}>
        <div className={styles.sessionIcon}>
          <CalendarIcon />
        </div>
        <div className={styles.sessionMeta}>
          <div className={styles.sessionKicker}>Your Next Session</div>
          <div className={styles.sessionTitle}>{booking.service.name}</div>
          <div className={styles.sessionDetailRow}>
            <span>
              <CalendarIcon />
              {formatHeroDate(booking.scheduledAt)}
            </span>
            <span>
              <ClockIcon />
              {formatCardTime(booking.scheduledAt)}
            </span>
          </div>
        </div>
        <div className={styles.nextSessionSide}>
          <span className={styles.statusPill}>{getHeroStatusLabel(booking)}</span>
          <ChevronRightIcon className={styles.chev} />
        </div>
      </div>

      {isGroup ? (
        <div className={styles.capacityBlock}>
          <div className={styles.capacityRow}>
            <div className={styles.capacityLabel}>
              <UsersIcon />
              {booked} / {booking.capacity} spots booked
            </div>
          </div>
          <div className={styles.capacityBar}>
            <div className={styles.capacityFill} style={{ width: `${fillPct}%` }} />
          </div>
        </div>
      ) : (
        <div className={styles.oneOnOne}>
          {booking.provider && (
            <InitialsAvatar name={booking.provider.name} photoUrl={booking.provider.avatarUrl} size="sm" />
          )}
          <div className={styles.oneOnOneText}>
            {booking.provider && <div className={styles.oneOnOneName}>{booking.provider.name}</div>}
            <div className={styles.oneOnOneLocation}>{formatLocationText(booking.location)}</div>
          </div>
        </div>
      )}
    </button>
  );
}
